// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Getting artefacts out of the tab.
 *
 * ONE PROGRAMMATIC DOWNLOAD PER USER GESTURE. EVER.
 *
 * Chromium's `download_request_limiter.cc` starts a tab at `ALLOW_ONE_DOWNLOAD`;
 * the first download is allowed and immediately flips the tab to
 * `PROMPT_BEFORE_DOWNLOAD`, so the second raises a "Download multiple files?"
 * bubble. If the user misses that bubble, artefacts two, three and four SILENTLY
 * NEVER ARRIVE — and there is no success signal on `<a download>.click()` to
 * notice with. The counter resets on user interaction, which is why the rule is
 * per-gesture rather than per-run.
 *
 * So: every artefact that would be a separate download goes into ONE zip, and
 * the download is triggered synchronously inside the click handler, never after
 * an await. Where `showSaveFilePicker` exists it is preferred outright — the
 * user chooses the location, the stream writes in constant memory, and the
 * limiter is not involved at all.
 */

import { makeZip } from 'client-zip';

export interface Artefact {
  readonly name: string;
  readonly text: string;
}

const encoder = new TextEncoder();

/**
 * Trigger a download.
 *
 * MUST be called synchronously from a click handler. The Blob is built from
 * parts rather than a joined string: peak memory during assembly is 1.18–1.30×
 * the source bytes, and on a phone that multiplier is the difference between an
 * artefact and a tab the operating system kills with no catchable exception.
 */
export function download(name: string, parts: BlobPart[], type = 'text/plain;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob(parts, { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Long enough for the download to have started, short enough not to pin a
  // large Blob in memory.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Several artefacts, one gesture, one file. */
export async function downloadBundleZip(name: string, artefacts: readonly Artefact[]): Promise<void> {
  const entries = artefacts.map((a) => ({
    name: a.name,
    lastModified: new Date(),
    input: encoder.encode(a.text),
  }));
  const blob = await new Response(makeZip(entries)).blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Copy something too large for one string comfortably, from parts.
 *
 * `ClipboardItem` takes a Blob, so a 40 MB bundle never has to exist as a single
 * JavaScript string. Falls back to `writeText` where `ClipboardItem` is absent.
 */
export async function copyParts(parts: readonly string[]): Promise<boolean> {
  const blob = new Blob([...parts], { type: 'text/plain' });
  try {
    if (typeof ClipboardItem === 'function' && navigator.clipboard?.write) {
      await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
      return true;
    }
  } catch {
    /* fall through to the text path */
  }
  return copyToClipboard(await blob.text());
}
